import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Instagram, MessageCircle, Globe } from "lucide-react";
import CardMockup from "./CardMockup";
import { EASE } from "./motion";

const STEPS = ["Tenez la carte", "Approchez le téléphone", "Découvrez"];

// Mini page digitale affichée sur l'écran du smartphone.
function PhoneScreen() {
  return (
    <div className="flex h-full flex-col items-center gap-2 rounded-[1.6rem] bg-white p-3 pt-5 text-nuit-900">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-nuit-900 font-display text-xs font-bold text-white">
        N
      </span>
      <p className="font-display text-[11px] font-bold">Votre Établissement</p>
      <div className="mt-1 flex w-full flex-col gap-1.5">
        {[
          { icon: Star, label: "Laisser un avis Google" },
          { icon: Instagram, label: "Suivre sur Instagram" },
          { icon: MessageCircle, label: "Écrire sur WhatsApp" },
          { icon: Globe, label: "Voir le site web" },
        ].map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="flex items-center gap-2 rounded-lg bg-brume px-2.5 py-1.5 text-[9px] font-medium"
          >
            <Icon className="h-3 w-3 text-electric" /> {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function NfcDemo({ className = "" }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={`relative ${className}`} data-testid="nfc-demo">
      <div className="relative mx-auto flex h-[380px] max-w-md items-end justify-center sm:h-[420px]">
        {/* Carte posée */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE }}
          className="relative z-10 mb-2 w-64 sm:w-72"
        >
          <CardMockup name="Votre Établissement" slogan={null} shine />
          {/* Ondes NFC au point de contact */}
          <div className="absolute -top-3 right-6 z-20">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 animate-nfc-ring rounded-full border-2 border-electric-300"
                style={{ animationDelay: `${i * 0.8}s` }}
              />
            ))}
          </div>
        </motion.div>

        {/* Smartphone qui approche */}
        <motion.div
          className="phone-mock absolute right-0 top-0 z-20 h-56 w-28 p-1.5 sm:h-64 sm:w-32"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          animate={{ x: [70, 6, 6, 70], y: [-24, 34, 34, -24], rotate: [8, -4, -4, 8] }}
          transition={{ duration: 5.2, times: [0, 0.35, 0.62, 1], repeat: Infinity, repeatDelay: 0.6, ease: "easeInOut" }}
        >
          <PhoneScreen />
        </motion.div>
      </div>

      {/* Légende animée */}
      <div className="mt-6 flex h-8 items-center justify-center gap-3">
        {STEPS.map((label, i) => (
          <span key={label} className="flex items-center gap-3">
            <AnimatePresence mode="wait">
              {step === i && (
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="font-display text-lg font-semibold text-white"
                >
                  {label}
                </motion.span>
              )}
            </AnimatePresence>
            {i < STEPS.length - 1 && step === i && (
              <span className="h-px w-6 bg-electric-300/60" aria-hidden="true" />
            )}
          </span>
        ))}
      </div>
      <div className="mt-2 flex justify-center gap-2">
        {STEPS.map((label, i) => (
          <span
            key={label}
            className={`h-1 rounded-full transition-all duration-500 ${step === i ? "w-8 bg-electric" : "w-3 bg-white/25"}`}
          />
        ))}
      </div>
    </div>
  );
}
